import { Flex, Skeleton, Text, useBreakpointValue } from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  type ExtensionNameMap,
  formatExtensionLabel,
} from '../hooks/useExtensionNames'
import { useReportingI18n } from '../i18n'
import {
  DropdownMultiSelect,
  MultiSelectItem,
} from './dropdown/DropdownMultiSelect'
import { MenuPlacement } from './dropdown/Menu'
import { ExtensionPills } from './ExtensionPills'

interface ExtensionMultiDropdownProps {
  selectedExtensions: string[]
  extensions: string[]
  extensionNameMap?: ExtensionNameMap
  isExtensionsLoading: boolean
  onSelectionChange?: (extensions: string[]) => void
  placeholder?: string
  width?: string
  maxSelection?: number
}

export const ExtensionMultiDropdown = ({
  selectedExtensions,
  extensions,
  extensionNameMap,
  isExtensionsLoading,
  onSelectionChange,
  placeholder,
  width = '12rem',
  maxSelection = 50,
}: ExtensionMultiDropdownProps) => {
  const { text } = useReportingI18n()
  const resolvedPlaceholder =
    placeholder ??
    text('extensionMultiDropdown.allExtensions', 'All Extensions')
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const onSelectionChangeRef = useRef(onSelectionChange)
  const pendingValuesRef = useRef<string[] | null>(null)

  const maxVisiblePills = useBreakpointValue(
    {
      base: 1,
      sm: 1,
      md: 2,
      lg: 3,
      xl: 4,
    },
    { fallback: 'md' },
  )

  onSelectionChangeRef.current = onSelectionChange

  const [pendingChanges, setPendingChanges] = useState<string[] | null>(null)

  const displayExtensions = pendingChanges ?? selectedExtensions

  const selectedValues = useMemo(() => displayExtensions, [displayExtensions])

  const selectedSet = useMemo(
    () => new Set(selectedExtensions),
    [selectedExtensions],
  )

  const selectItems: MultiSelectItem<string>[] = useMemo(() => {
    const map = extensionNameMap ?? new Map()
    const mapped = extensions.map((ext) => {
      const label = formatExtensionLabel(ext, map)
      return {
        value: ext,
        label,
        searchLabel: `${ext} ${map.get(ext) ?? ''}`.toLowerCase(),
        content: (
          <Text
            fontSize="14px"
            fontWeight="400"
            color="fg.default"
            lineHeight="1.2"
          >
            {label}
          </Text>
        ),
      }
    })

    const selected: MultiSelectItem<string>[] = []
    const unselected: MultiSelectItem<string>[] = []
    for (const item of mapped) {
      ;(selectedSet.has(item.value) ? selected : unselected).push(item)
    }
    const byLabel = (a: MultiSelectItem<string>, b: MultiSelectItem<string>) =>
      a.label.localeCompare(b.label)
    return [...selected.sort(byLabel), ...unselected.sort(byLabel)]
  }, [extensions, extensionNameMap, selectedSet])

  const handleSelectionChange = useCallback((values: string[]) => {
    setPendingChanges(values)
    pendingValuesRef.current = values

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      setPendingChanges(null)
      pendingValuesRef.current = null
      onSelectionChangeRef.current?.(values)
    }, 800)
  }, [])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
        if (pendingValuesRef.current !== null) {
          onSelectionChangeRef.current?.(pendingValuesRef.current)
          pendingValuesRef.current = null
        }
      }
    }
  }, [])

  const handleRemoveExtension = useCallback(
    (extToRemove: string) => {
      const updated = displayExtensions.filter((e) => e !== extToRemove)

      setPendingChanges(updated)
      pendingValuesRef.current = updated

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        setPendingChanges(null)
        pendingValuesRef.current = null
        onSelectionChangeRef.current?.(updated)
      }, 800)
    },
    [displayExtensions],
  )

  const handleClearAll = useCallback(() => {
    setPendingChanges([])
    pendingValuesRef.current = []
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      setPendingChanges(null)
      pendingValuesRef.current = null
      onSelectionChangeRef.current?.([])
    }, 800)
  }, [])

  if (isExtensionsLoading) {
    return <Skeleton width={width} height="40px" borderRadius="md" />
  }

  return (
    <Flex gap="0.5rem" alignItems="center" flexWrap="wrap">
      <Flex width={width}>
        <DropdownMultiSelect
          placeholder={resolvedPlaceholder}
          selectedValues={selectedValues}
          items={selectItems}
          onSelectionChange={handleSelectionChange}
          searchable
          maxSelection={maxSelection}
          menuOptions={{
            width: '14rem',
            maxHeight: '35vh',
            placement: MenuPlacement.BottomRight,
          }}
        />
      </Flex>

      <ExtensionPills
        selectedExtensions={displayExtensions}
        extensionNameMap={extensionNameMap}
        onRemoveExtension={handleRemoveExtension}
        onClearAll={handleClearAll}
        maxVisiblePills={maxVisiblePills}
      />
    </Flex>
  )
}
